import { unstable_batchedUpdates } from "react-dom";

/**
 * THE TAB COMING BACK, told to every contact reader AT ONCE.
 *
 * A card re-checks a stale answer when its tab becomes visible again (a listener in a
 * background tab may have been throttled). Each card used to add its own `visibilitychange`
 * listener, and React 17 does not batch updates made from a native listener — nor does the
 * browser wait between them: every listener's callback is followed by a microtask checkpoint.
 * So each card re-rendered, asked and SENT on its own, and a listings board (a seller badge per
 * listing, 200 a page) sent 200 to 317 single requests in a minute every time its realtor came
 * back to the tab (measured 2026-10-19), although phones asked in the same tick leave together.
 *
 * Here ONE document listener tells every subscriber inside ONE `unstable_batchedUpdates`: their
 * re-renders commit together, their effects run together, and what they ask leaves together.
 * A test that dispatches the event inside `act()` gets that batching from `act` itself — which
 * is why the per-card listeners passed every test and failed in the browser.
 */
const subscribers = new Set<() => void>();

const onVisibilityChange = (): void => {
  if (document.visibilityState !== "visible") return;
  // A snapshot: a subscriber's re-render may subscribe or unsubscribe others.
  const notify = [...subscribers];
  unstable_batchedUpdates(() => {
    notify.forEach((subscriber) => {
      try {
        subscriber();
      } catch (error) {
        // One reader's failure must not keep the others from re-checking.
        // eslint-disable-next-line no-console
        console.error("tabVisibility: a reader failed to re-check", error);
      }
    });
  });
};

/** Calls `onVisible` each time the tab becomes visible again. Returns the unsubscribe. */
export const subscribeTabVisible = (onVisible: () => void): (() => void) => {
  if (subscribers.size === 0) document.addEventListener("visibilitychange", onVisibilityChange);
  subscribers.add(onVisible);
  return () => {
    subscribers.delete(onVisible);
    if (subscribers.size === 0) document.removeEventListener("visibilitychange", onVisibilityChange);
  };
};
